// prettier-ignore
import React from "react";

import { capitalize, isArray, isEmpty } from "lodash";
import FIcon from "@components/FIcon";
import { decorationColor } from "./cardUtilities";
import { ContentReviewDeliverable } from "@types";
import { CONTENT_REVIEW_STATUS } from "@pages/campaigns/content-review/statuses";

export const defaultInvoicePaymentCopy = (projectNumber: string | number) => {
  return `<p>We invite you to submit invoices upon completion of all deliverables and requirements outlined in your contract or according to the agreed-upon invoicing schedule described in this campaign brief. If you have any questions before sending your invoice, please contact your campaign manager directly. Billing inquiries should be addressed to <a href="mailto:billing@fohr.co">billing@fohr.co</a> and your campaign manager.</p>
<p>Please be sure to reference the Project Number ${projectNumber}.</p>
<p><strong>All invoices must be submitted to <a href="mailto:billing@fohr.co">billing@fohr.co</a>.</strong> In addition to the invoice, influencers and agencies located in the United States must submit a <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank">W9 form</a>. Influencers and agencies outside the United States will submit a <a href="https://www.irs.gov/pub/irs-pdf/fw8ben.pdf" target="_blank">W-8BEN form</a>.</p>
<p>In addition to the Project Number, it is essential that invoices include several additional pieces of information in order for us to process your invoice efficiently and on a timely basis. These are our "acceptance criteria."</p>
<p>The details we require are:</p>
<ul><li>Project Number</li><li>Influencer's legal name</li><li>Agency legal name, if applicable</li><li>Email address</li><li>Invoice date</li><li>Invoice amount</li><li>Campaign name (the name referenced in this campaign brief page)</li><li>Campaign manager's name</li><li>PDF Format</li></ul>
<p>Invoices that meet the above acceptance criteria will be paid 60 days after receipt or according to the payment terms specified in this campaign brief. Invoices not meeting the acceptance criteria will be rejected and you will be asked to re-submit your invoice with the required information. Invoices submitted before the completion of deliverables and contract requirements will be rejected. Payments are remitted in United States Dollars, and Fohr is not responsible for any fees, including those arising from foreign currency exchange.</p>`;
};

export const defaultPaymentCopy =
  "<p>Payment of Compensation will be made by Fohr to Influencer according to the payment schedule set forth in the Influencer Services Deal Memo and accompanying Standard Terms and Conditions (found in the 'Contracts' tab)</p>";

/**
 * Get the status icon for content review status
 * @param status The status string
 * @returns JSX.Element - The icon component for the status
 */
export const getStatusIcon = (status) => {
  switch (status) {
    case "not_started":
      return (
        <FIcon
          icon="calendar-blank"
          size={12}
          color="#FFFFFF"
          className="-mt-[2px]"
        />
      );
    case "not_submitted":
      return (
        <FIcon icon="pencil" size={16} color="#FFFFFF" className="-mt-[1px]" />
      );
    case "admin_review":
      return (
        <FIcon icon="eye" size={16} color="#FFFFFF" className="-mt-[1px]" />
      );
    case "admin_changes":
      return (
        <FIcon
          icon="warning-2"
          size={16}
          color="#FFFFFF"
          className="-mt-[1px]"
        />
      );
    case "influencer_changes":
      return (
        <FIcon
          icon="warning-2"
          size={16}
          color="#FFFFFF"
          className="-mt-[1px]"
        />
      );
    case "approved":
      return (
        <FIcon
          icon="checkmark"
          size={16}
          color="#FFFFFF"
          className="-mt-[1px]"
        />
      );
    default:
      return (
        <FIcon
          icon="calendar-blank"
          size={14}
          color="#FFFFFF"
          className="-mt-[1px]"
        />
      );
  }
};

/**
 * Get the human-readable label for content review status
 * @param status The status string
 * @returns string - The label for the status
 */
export const getStatusLabel = (status) => {
  switch (status) {
    case CONTENT_REVIEW_STATUS.NOT_STARTED:
      return "Not Started";
    case CONTENT_REVIEW_STATUS.NOT_SUBMITTED:
      return "Draft";
    case CONTENT_REVIEW_STATUS.ADMIN_REVIEW:
      return "Admin Review";
    case CONTENT_REVIEW_STATUS.ADMIN_CHANGES:
      return "Changes Requested";
    case CONTENT_REVIEW_STATUS.INFLUENCER_CHANGES:
      return "Changes Requested";
    case CONTENT_REVIEW_STATUS.APPROVED:
      return "Approved";
    default:
      return "Not Started";
  }
};

/**
 * Maps API status values to decorationColor values for consistent coloring
 * @param status The API status string
 * @returns string - The corresponding decorationColor status value
 */
export const mapStatusForColor = (status) => {
  switch (status) {
    case "approved":
      return "approved";
    case "admin_review":
      return "pending review";
    case "admin_changes":
      return "review_rejected";
    case "influencer_changes":
      return "review_rejected";
    case "not_submitted":
      return "draft";
    case "not_started":
      return "";
    default:
      return status;
  }
};

/**
 * Render a status badge with icon and label
 * @param status The status string
 * @returns JSX.Element - A status badge with appropriate styling
 */
export const getStatusBadge = (status) => {
  const mappedStatus = mapStatusForColor(status);
  const statusColorClass = decorationColor(mappedStatus);

  return (
    <span
      className={`w-max inline-flex items-center gap-[8px] px-3 pt-[4px] pb-[3px] rounded-full text-xs font-medium ${statusColorClass} text-white`}
    >
      {getStatusLabel(status)}
    </span>
  );
};

export const displayCounts = (post) => {
  const data = post.data || post;

  const { media_type } = data;

  const properties = ["hashtags", "geotags", "mentions"];

  const counts = properties.reduce((result, property) => {
    const value = data[property];

    const count = value
      ? isArray(value)
        ? value.length
        : value.split(" ").length
      : 0;

    const label = `${capitalize(property.slice(0, -1))}${count === 1 ? "" : "s"}`;

    if (!count) {
      return result;
    }

    return [
      ...result,
      {
        count,
        label,
      },
    ];
  }, []);

  // Only render if there's actual content to display
  if (!media_type && isEmpty(counts)) {
    return null;
  }

  return (
    <div className="flex mt-[3px] flex-row justify-start items-center text-sm text-neutral_600 capitalize">
      {media_type && (
        <p className="leading-[14px] h-[14px] pr-2">{media_type}</p>
      )}
      {counts.map(({ label, count }) => (
        <p className="border-neutral_500 px-2 leading-[14px] h-[14px]">
          {count} {label}
        </p>
      ))}
    </div>
  );
};

export const findNestedChanges = (newData, originalData, changes) => {
  Object.keys(newData).forEach((key) => {
    const newValue = newData[key];
    const originalValue = originalData[key];

    if (
      typeof newValue === "object" &&
      newValue !== null &&
      !Array.isArray(newValue) &&
      typeof originalValue === "object" &&
      originalValue !== null
    ) {
      const nestedChanges = {};
      findNestedChanges(newValue, originalValue, nestedChanges);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = nestedChanges;
      }
    } else if (!isEqual(newValue, originalValue)) {
      changes[key] = newValue;
    }
  });
};

// Helper function to check equality, including handling of null and empty string
const isEqual = (value1, value2) => {
  if (value1 === value2) return true;
  if (value1 === "" && value2 === null) return true;
  if (value1 === null && value2 === "") return true;
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return JSON.stringify(value1) === JSON.stringify(value2);
  }
  return false;
};

export const reformData = (data) => {
  const result = {};
  for (const key in data) {
    if (key.includes(".")) {
      const keys = key.split(".");
      keys.reduce((acc, part, index) => {
        if (index === keys.length - 1) {
          acc[part] = data[key];
        } else {
          acc[part] = acc[part] || {};
        }
        return acc[part];
      }, result);
    } else {
      result[key] = data[key];
    }
  }
  return result;
};

export const getDeliverableStatus = (
  deliverable: ContentReviewDeliverable
): CONTENT_REVIEW_STATUS => {
  const { content } = deliverable;

  const { rounds = [], status } = content || {};

  return isEmpty(rounds)
    ? CONTENT_REVIEW_STATUS.NOT_STARTED
    : (status as CONTENT_REVIEW_STATUS);
};
